/**
 * Éditions faites côté client (pas de route serveur pour elles) :
 * normaliser, couper, réparer une sélection, extraire une boucle.
 * Toutes renvoient un nouvel AudioBuffer — l'original n'est jamais modifié.
 */
import { getContext } from './context';

function make(channels: number, length: number, sampleRate: number): AudioBuffer {
  return getContext().createBuffer(channels, Math.max(1, length), sampleRate);
}

export function slice(buffer: AudioBuffer, from: number, to: number): AudioBuffer {
  const sr = buffer.sampleRate;
  const a = Math.max(0, Math.floor(from * sr));
  const b = Math.min(buffer.length, Math.floor(to * sr));
  const len = Math.max(1, b - a);
  const out = make(buffer.numberOfChannels, len, sr);
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    out.getChannelData(c).set(buffer.getChannelData(c).subarray(a, a + len));
  }
  return out;
}

export function normalize(buffer: AudioBuffer, targetDb = -1): AudioBuffer {
  let peak = 0;
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const d = buffer.getChannelData(c);
    for (let i = 0; i < d.length; i++) {
      const v = Math.abs(d[i]);
      if (v > peak) peak = v;
    }
  }
  if (peak < 1e-6) return buffer;
  const gain = Math.pow(10, targetDb / 20) / peak;
  const out = make(buffer.numberOfChannels, buffer.length, buffer.sampleRate);
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const src = buffer.getChannelData(c);
    const dst = out.getChannelData(c);
    for (let i = 0; i < src.length; i++) dst[i] = src[i] * gain;
  }
  return out;
}

/** Supprime la sélection avec un court fondu de raccord. */
export function cut(buffer: AudioBuffer, from: number, to: number): AudioBuffer {
  const sr = buffer.sampleRate;
  const a = Math.max(0, Math.floor(from * sr));
  const b = Math.min(buffer.length, Math.floor(to * sr));
  if (b <= a) return buffer;
  const fade = Math.min(Math.floor(sr * 0.008), a, buffer.length - b);
  const len = buffer.length - (b - a);
  const out = make(buffer.numberOfChannels, len, sr);

  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const src = buffer.getChannelData(c);
    const dst = out.getChannelData(c);
    dst.set(src.subarray(0, a), 0);
    dst.set(src.subarray(b), a);
    for (let i = 0; i < fade; i++) {
      const k = i / fade;
      const j = a - fade + i;
      if (j >= 0 && j < dst.length) dst[j] *= 1 - k * 0.5;
      const j2 = a + i;
      if (j2 < dst.length) dst[j2] *= 0.5 + k * 0.5;
    }
  }
  return out;
}

/**
 * « Réparer » : remplace la sélection par un fondu croisé entre ce qui
 * précède et ce qui suit — enlève un clic, une toux, un bruit isolé.
 */
export function repair(buffer: AudioBuffer, from: number, to: number): AudioBuffer {
  const sr = buffer.sampleRate;
  const a = Math.max(0, Math.floor(from * sr));
  const b = Math.min(buffer.length, Math.floor(to * sr));
  const len = b - a;
  if (len <= 0) return buffer;

  const out = make(buffer.numberOfChannels, buffer.length, sr);
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const src = buffer.getChannelData(c);
    const dst = out.getChannelData(c);
    dst.set(src);
    for (let i = 0; i < len; i++) {
      const k = i / len;
      const before = src[Math.max(0, a - len + i)];
      const after = src[Math.min(buffer.length - 1, b + i)];
      dst[a + i] = before * (1 - k) + after * k;
    }
  }
  return out;
}
